/**
 * 採用データ(現行シート「CC採用データ」の CSV)の読み取り(2026-10-08。D-197)。
 *
 * シートの形(2026-10-08 に実ファイルで確認):
 *  - 見出しの行に「ステータス」「名前」「種別」…「稼働日」がある。種別 = 給湯器 / 不用品
 *  - その上の行に日付(MM/DD)が並び、日ごとに「アポ・時間・h」の3列。
 *    **日付の見出しと数字の列が数列ずれている**ことがあるので、h ≒ アポ ÷ 時間 になるずれ幅を数字から探す
 *  - 給湯器と不用品は別の行(不用品のIDの人は名前の末尾に W)。同じ人・同じ種別の行が2つあれば足す
 *  - 時間は OPレポートの 受付＋通話＋後処理(2026-10-08 照合で一致)
 */
import type { BusinessType, DateStr, Operator } from '@core/lib/types';
import { SHEET_UNTIL, trackKey, type SheetDays } from './training';

export type SheetRow = {
  name: string;
  business: BusinessType;
  startDate: DateStr | null;
  days: SheetDays;
};

export type SheetParseResult = { rows: SheetRow[]; dateFrom: DateStr | null; dateTo: DateStr | null; offset: number };

const DATE_RE = /^(\d{1,2})\/(\d{1,2})$/;

const num = (v: string | undefined): number => {
  const n = Number(String(v ?? '').replace(/,/g, '').trim());
  return Number.isFinite(n) ? n : 0;
};

/** 名前の空白を消す。照合では末尾の W(不用品のID)も外す */
export const normalizeName = (name: string) => name.replace(/[\s　]/g, '');
export const matchName = (name: string) => normalizeName(name).replace(/[WＷ]$/, '');

/**
 * 名前でマスタの人を探す。**W も含めた名前が同じ人を先に探す**(マスタに「山田太郎」と「山田太郎W」が
 * 別の人として登録されていることがあるため。2026-10-09)。見つからなければ末尾の W を外して探す。
 * 同じ名前が2人以上いるときは在籍中の1人に絞れればその人。決まらなければ候補の数を返す
 */
export function findOperatorByName(name: string, operators: Operator[]): { op: Operator | null; count: number } {
  const pick = (cands: Operator[]) => {
    const active = cands.filter((o) => o.status === 'active');
    return cands.length === 1 ? cands[0]! : active.length === 1 ? active[0]! : null;
  };
  const exact = operators.filter((o) => normalizeName(o.name) === normalizeName(name));
  const exactOp = pick(exact);
  if (exactOp) return { op: exactOp, count: exact.length };
  const loose = operators.filter((o) => matchName(o.name) === matchName(name));
  return { op: pick(loose), count: loose.length };
}

const toBusiness = (v: string): BusinessType | null => {
  const s = v.trim();
  if (s === '給湯器') return 'kyutoki';
  if (s === '不用品' || s === 'ホワイト') return 'fuyohin';
  return null;
};

/** M/D を年付きの日付にする。今月より2か月以上先の月は前の年とみなす */
export function toDate(md: string, today: DateStr): DateStr | null {
  const m = DATE_RE.exec(md.trim());
  if (!m) return null;
  const month = Number(m[1]);
  const day = Number(m[2]);
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;
  const ty = Number(today.slice(0, 4));
  const tm = Number(today.slice(5, 7));
  const year = month > tm + 1 ? ty - 1 : ty;
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

export function parseRecruitSheet(table: string[][], today: DateStr): SheetParseResult {
  const headIdx = table.findIndex((r) => r.some((c) => c.trim() === '名前') && r.some((c) => c.trim() === '種別') && r.some((c) => c.trim() === '稼働日'));
  if (headIdx < 0) throw new Error('「名前」「種別」「稼働日」の見出しが見つかりません。採用データのCSVか確認してください');
  const head = table[headIdx]!.map((c) => c.trim());
  const nameCol = head.indexOf('名前');
  const typeCol = head.indexOf('種別');
  const startCol = head.indexOf('稼働日');

  // 日付の見出しの行(MM/DD が10個以上並ぶ行)
  let dateRow: string[] | undefined;
  for (let i = 0; i < table.length; i++) {
    if (table[i]!.filter((c) => DATE_RE.test(c.trim())).length >= 10) {
      dateRow = table[i];
      break;
    }
  }
  if (!dateRow) throw new Error('日付(MM/DD)の見出しの行が見つかりません');
  const dateCols = dateRow.map((c, i) => ({ c: c.trim(), i })).filter((x) => DATE_RE.test(x.c));

  const body = table.slice(headIdx + 1).filter((r) => (r[nameCol] ?? '').trim() !== '' && toBusiness(r[typeCol] ?? '') !== null);

  // 見出しと数字のずれ幅: h ≒ アポ ÷ 時間 が一番多く成り立つずれ
  let offset = 0;
  let best = -1;
  for (let off = 0; off <= 4; off++) {
    let score = 0;
    for (const r of body) {
      for (const { i } of dateCols) {
        const a = num(r[i + off]);
        const t = num(r[i + off + 1]);
        const h = num(r[i + off + 2]);
        if (t > 0 && Math.abs(a / t - h) <= 0.011) score++;
      }
    }
    if (score > best) {
      best = score;
      offset = off;
    }
  }

  const rows: SheetRow[] = [];
  let dateFrom: DateStr | null = null;
  let dateTo: DateStr | null = null;
  for (const r of body) {
    const days: SheetDays = {};
    for (const { c, i } of dateCols) {
      const date = toDate(c, today);
      if (!date) continue;
      const a = num(r[i + offset]);
      const t = num(r[i + offset + 1]);
      if (a === 0 && t === 0) continue;
      days[date] = [Math.round(a), Math.round(t * 100) / 100];
      if (!dateFrom || date < dateFrom) dateFrom = date;
      if (!dateTo || date > dateTo) dateTo = date;
    }
    rows.push({ name: (r[nameCol] ?? '').trim(), business: toBusiness(r[typeCol] ?? '')!, startDate: toDate(r[startCol] ?? '', today), days });
  }
  return { rows, dateFrom, dateTo, offset };
}

export type SheetImportEntry = {
  key: string;
  opId: string;
  opCode: string;
  operatorName: string;
  sheetName: string;
  business: BusinessType;
  startDate: DateStr | null;
  /** SHEET_UNTIL より前の日だけ */
  days: SheetDays;
  dayCount: number;
  hours: number;
  appointments: number;
};

export type SheetImportPlan = {
  entries: SheetImportEntry[];
  /** マスタの在籍者に名前が見つからない・2人以上いる行 */
  unmatched: { name: string; business: BusinessType; reason: string }[];
};

/** 採用データの行を オペレーターマスタの人に結び付ける(名前で照合。末尾の W と空白は無視) */
export function planSheetImport(rows: SheetRow[], operators: Operator[]): SheetImportPlan {
  const merged = new Map<string, SheetImportEntry>();
  const unmatched: SheetImportPlan['unmatched'] = [];
  for (const row of rows) {
    const { op, count } = findOperatorByName(row.name, operators);
    if (!op) {
      unmatched.push({ name: row.name, business: row.business, reason: count === 0 ? 'マスタに同じ名前の人がいません' : `マスタに同じ名前の人が${count}人います` });
      continue;
    }
    const key = trackKey(op.id, row.business);
    const e = merged.get(key) ?? {
      key,
      opId: op.id,
      opCode: op.op_code,
      operatorName: op.name,
      sheetName: row.name,
      business: row.business,
      startDate: null,
      days: {},
      dayCount: 0,
      hours: 0,
      appointments: 0,
    };
    if (e.sheetName !== row.name && !e.sheetName.split('・').includes(row.name)) e.sheetName = `${e.sheetName}・${row.name}`;
    if (row.startDate && (!e.startDate || row.startDate < e.startDate)) e.startDate = row.startDate;
    for (const [date, [a, t]] of Object.entries(row.days)) {
      if (date >= SHEET_UNTIL) continue;
      const cur = e.days[date] ?? [0, 0];
      e.days[date] = [cur[0] + a, Math.round((cur[1] + t) * 100) / 100];
    }
    merged.set(key, e);
  }
  const entries = Array.from(merged.values()).map((e) => {
    const vals = Object.values(e.days);
    return {
      ...e,
      dayCount: vals.length,
      appointments: vals.reduce((s, v) => s + v[0], 0),
      hours: Math.round(vals.reduce((s, v) => s + v[1], 0) * 100) / 100,
    };
  });
  entries.sort((a, b) => a.opCode.localeCompare(b.opCode) || a.business.localeCompare(b.business));
  return { entries, unmatched };
}

/** 稼働開始日のまとめて変更(2026-10-09 発注者指示): 貼り付けた1行の結果 */
export type StartDateChange = {
  line: number;
  /** 貼り付けた名前(前後の空白を除く) */
  name: string;
  business: BusinessType;
  date: DateStr | null;
  opId: string | null;
  opCode: string;
  operatorName: string;
  key: string | null;
  /** 今の稼働開始日 */
  current: DateStr | null;
  /** change=変える / same=同じ日付 / error=変えない(理由は reason) */
  status: 'change' | 'same' | 'error';
  reason: string;
};

const FULL_DATE_RE = /^(\d{4})[/-](\d{1,2})[/-](\d{1,2})$/;

/** 「5/11」「2026/5/11」「2026-05-11」を日付にする(読めなければ null) */
export function toStartDate(v: string, today: DateStr): DateStr | null {
  const s = v.trim().replace(/[０-９／]/g, (c) => (c === '／' ? '/' : String.fromCharCode(c.charCodeAt(0) - 0xfee0)));
  const full = FULL_DATE_RE.exec(s);
  if (full) {
    const [y, m, d] = [Number(full[1]), Number(full[2]), Number(full[3])];
    if (m < 1 || m > 12 || d < 1 || d > 31) return null;
    return `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
  }
  return toDate(s, today);
}

/**
 * 「名前<タブ>稼働日」の貼り付け(スプレッドシートから2列をコピー)を読み、マスタの在籍者に結び付ける。
 * 名前の末尾が W なら不用品、それ以外は給湯器(採用データのシートと同じ決まり)。見出しの行・空行は飛ばす。
 */
export function planStartDateChanges(text: string, operators: Operator[], existing: { id: string; start_date: DateStr | null }[], today: DateStr): StartDateChange[] {
  const activeOps = operators.filter((o) => o.status === 'active');
  const current = new Map(existing.map((d) => [d.id, d.start_date]));
  const out: StartDateChange[] = [];
  const seen = new Map<string, number>();
  text.split(/\r?\n/).forEach((raw, i) => {
    if (raw.trim() === '') return;
    // タブ区切り。タブが無ければ最後の空白で分ける
    const cells = raw.includes('\t') ? raw.split('\t') : [raw.replace(/\s+\S+\s*$/, ''), raw.trim().split(/\s+/).pop() ?? ''];
    const name = (cells[0] ?? '').trim().replace(/^[\s　]+|[\s　]+$/g, '');
    const dateText = (cells[1] ?? '').trim();
    if (name === '名前' || /稼働/.test(dateText)) return; // 見出し
    const business: BusinessType = /[WＷ]$/.test(normalizeName(name)) ? 'fuyohin' : 'kyutoki';
    const date = toStartDate(dateText, today);
    const base = { line: i + 1, name, business, date, opId: null, opCode: '', operatorName: '', key: null, current: null } as const;
    if (!date) {
      out.push({ ...base, status: 'error', reason: `日付なし(${dateText || '空欄'})` });
      return;
    }
    const { op, count } = findOperatorByName(name, activeOps);
    if (!op) {
      out.push({ ...base, status: 'error', reason: count === 0 ? '在籍者にいない' : `同じ名前が${count}人` });
      return;
    }
    const key = trackKey(op.id, business);
    const cur = current.get(key) ?? null;
    const row = { ...base, opId: op.id, opCode: op.op_code, operatorName: op.name, key, current: cur };
    const dup = seen.get(key);
    if (dup !== undefined) {
      out.push({ ...row, status: 'error', reason: `${dup}行目と重複` });
      return;
    }
    seen.set(key, i + 1);
    out.push({ ...row, status: cur === date ? 'same' : 'change', reason: '' });
  });
  return out;
}

/** 面談結果のシート(「コールセンター 面談結果」の CSV)の1行(2026-10-09 発注者指示) */
export type InterviewSheetRow = { line: number; name: string; business: BusinessType; date: DateStr | null; report: string };

/**
 * 面談結果のシートを読む。見出しの行(「氏名」「商材」「面談日」がある行)から下の、左側の表だけを使う
 * (右側は面談前14日間などの別の集計で、使わない)。氏名が空の行で終わり。
 * 使う列: 氏名・商材(給湯器/不用品)・面談日・改善内容・次回確認事項。シートの前後の数字は使わない(このシステムの前後15hで比べる)
 */
export function parseInterviewSheet(table: string[][], today: DateStr): InterviewSheetRow[] {
  const headIdx = table.findIndex((r) => r.some((c) => c.trim() === '氏名') && r.some((c) => c.trim() === '商材') && r.some((c) => c.trim() === '面談日'));
  if (headIdx < 0) throw new Error('「氏名」「商材」「面談日」の見出しが見つかりません。面談結果のCSVか確認してください');
  const head = table[headIdx]!.map((c) => c.trim());
  const nameCol = head.indexOf('氏名');
  const typeCol = head.indexOf('商材');
  const dateCol = head.indexOf('面談日');
  const reportCol = head.findIndex((c) => c.startsWith('改善内容'));
  const out: InterviewSheetRow[] = [];
  for (let i = headIdx + 1; i < table.length; i++) {
    const r = table[i]!;
    const name = (r[nameCol] ?? '').trim();
    if (!name) break;
    const business = toBusiness(r[typeCol] ?? '');
    if (!business) continue;
    out.push({ line: i + 1, name, business, date: toStartDate(r[dateCol] ?? '', today), report: reportCol >= 0 ? (r[reportCol] ?? '').trim() : '' });
  }
  return out;
}

export type InterviewImportEntry = InterviewSheetRow & {
  opId: string | null;
  opCode: string;
  operatorName: string;
  /** add=取り込む / dup=同じ人・カテゴリ・日付の面談が登録済み / error=取り込まない */
  status: 'add' | 'dup' | 'error';
  reason: string;
};

/**
 * 面談結果の行をマスタの人に結び付ける。不用品の行は「名前W」の人を先に探す(マスタに W 付きで別に登録されている人がいるため)。
 * 同じ人・カテゴリ・日付の面談がもう登録されていれば取り込まない(何度取り込んでも二重にならない)
 */
export function planInterviewImport(rows: InterviewSheetRow[], operators: Operator[], existing: { op_id: string; business: BusinessType; date: DateStr; kind: string }[]): InterviewImportEntry[] {
  const active = operators.filter((o) => o.status === 'active');
  const done = new Set(existing.filter((r) => r.kind === 'interview').map((r) => `${r.op_id}_${r.business}_${r.date}`));
  const seen = new Set<string>();
  return rows.map((row) => {
    const base = { ...row, opId: null, opCode: '', operatorName: '' };
    if (!row.date) return { ...base, status: 'error' as const, reason: '面談日を読めません' };
    let found = row.business === 'fuyohin' && !/[WＷ]$/.test(normalizeName(row.name)) ? findOperatorByName(`${row.name}W`, active) : { op: null, count: 0 };
    if (!found.op || normalizeName(found.op.name) !== `${normalizeName(row.name)}W`) found = findOperatorByName(row.name, active);
    const op = found.op;
    if (!op) return { ...base, status: 'error' as const, reason: found.count === 0 ? '在籍者にいない' : `同じ名前が${found.count}人` };
    const withOp = { ...base, opId: op.id, opCode: op.op_code, operatorName: op.name };
    const key = `${op.id}_${row.business}_${row.date}`;
    if (done.has(key)) return { ...withOp, status: 'dup' as const, reason: '登録済み' };
    if (seen.has(key)) return { ...withOp, status: 'dup' as const, reason: 'CSVの中で重複' };
    seen.add(key);
    return { ...withOp, status: 'add' as const, reason: '' };
  });
}
